import { Ref, ref } from 'vue'
import {
  Web3Auth,
  decodeToken,
  type Web3AuthOptions,
} from '@web3auth/single-factor-auth'
import {
  ADAPTER_EVENTS,
  CHAIN_NAMESPACES,
  WEB3AUTH_NETWORK,
} from '@web3auth/base'
import { EthereumPrivateKeyProvider } from '@web3auth/ethereum-provider'
import { PasskeysPlugin } from '@web3auth/passkeys-sfa-plugin'
import { initializeApp } from 'firebase/app'
import {
  GoogleAuthProvider,
  getAuth,
  signInWithPopup,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from 'firebase/auth'
import { useRouter } from 'vue-router'
import { lukso } from 'viem/chains'
import { numberToHex } from 'web3-utils'

import RPC from '../components/views/Onboard/EmbeddedAccount/web3RPC'
import { shouldSupportPasskey } from '../components/views/Onboard/EmbeddedAccount/utils'
import { useAppStore } from '../store/AppStore'

import type { IProvider } from '@web3auth/base'

const PROVIDER_NOT_INITIALIZED = 'Provider not initialized'

const clientId =
  'BI-Jv6Nm8A2sw_a5Rcp9CefEf0wu7tBHGEYIYr-jZhUQbx18U8SWSZ4aRTWm3pUr5UyO0zfQp45m9qSf5uyE0TQ' // get from https://dashboard.web3auth.io
// const passkeys = ref<unknown[]>([])
const email = ref<string>(localStorage.getItem('emailForSignIn') || '')
const provider = ref<IProvider | undefined>()
const router = useRouter()
const chain = lukso
const chainConfig = {
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: numberToHex(chain.id),
  rpcTarget: chain.rpcUrls.default.http[0],
  // Avoid using public rpcTarget in production.
  // Use services like Infura, Quicknode etc
  displayName: chain.name,
  blockExplorerUrl: chain.blockExplorers.default.url,
  ticker: chain.nativeCurrency.symbol,
  tickerName: chain.nativeCurrency.name,
  logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.png',
}

const privateKeyProvider = new EthereumPrivateKeyProvider({
  config: { chainConfig },
})

const firebaseConfig = {
  apiKey: 'AIzaSyBi2z7zTzWUwghvuhMUE_xm3dmljc_W0kk',
  authDomain: 'service-web3auth.firebaseapp.com',
  projectId: 'service-web3auth',
  storageBucket: 'service-web3auth.appspot.com',
  messagingSenderId: '222035208796',
  appId: '1:222035208796:web:d3dbdedf1426cc01fc9499',
}

const app = initializeApp(firebaseConfig)

const web3AuthOptions: Web3AuthOptions = {
  clientId,
  web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_DEVNET,
  usePnPKey: false, // Setting this to true returns the same key as PnP Web SDK, By default, this SDK returns CoreKitKey.
  privateKeyProvider,
}

const web3auth = new Web3Auth(web3AuthOptions)

const domain = new URL(document.location.origin).hostname

const pkPlugin = new PasskeysPlugin({
  rpID: domain,
  rpName: 'UE Embedded Wallet',
})
web3auth.addPlugin(pkPlugin)

const verifier = 'firebase-verifier-google'

let inited: Promise<Ref<IProvider | undefined> | undefined> | undefined

const init = async (): Promise<Ref<IProvider | undefined> | undefined> => {
  inited = inited || _init()
  return await inited
}

const _init = async (): Promise<Ref<IProvider | undefined> | undefined> => {
  try {
    await web3auth.init()
    provider.value = web3auth.provider || undefined
    if (web3auth.status === ADAPTER_EVENTS.CONNECTED) {
      loggedIn.value = true
    }
    console.log('web3auth initialized')
    return provider
  } catch (error) {
    console.error(error)
  }

  const auth = getAuth()
  if (isSignInWithEmailLink(auth, window.location.href)) {
    console.log('isSignInWithEmailLink', window.location.href)
    // Additional state parameters can also be passed via URL.
    // This can be used to continue the user's intended action before triggering
    // the sign-in operation.
    // Get the email if available. This should be available if the user completes
    // the flow on the same device where they started it.
    let email = window.localStorage.getItem('emailForSignIn')
    if (!email) {
      // User opened the link on a different device. To prevent session fixation
      // attacks, ask the user to provide the associated email again. For example:
      email = window.prompt('Please provide your email for confirmation')
      if (!email) {
        return
      }
    }
    // The client SDK will parse the code from the link for you.
    signInWithEmailLink(auth, email, window.location.href)
      .then(async result => {
        // You can access the new user by importing getAdditionalUserInfo
        // and calling it with result:
        // getAdditionalUserInfo(result)
        // You can access the user's profile via:
        // getAdditionalUserInfo(result)?.profile
        // You can check if the user is new or existing:
        // getAdditionalUserInfo(result)?.isNewUser
        const idToken = await result.user.getIdToken(true)
        const { payload } = decodeToken(idToken)

        const web3authProvider = await web3auth.connect({
          verifier,
          verifierId: (payload as { sub: string }).sub,
          idToken,
        })

        if (web3authProvider) {
          loggedIn.value = true
          provider.value = web3authProvider
        }
        router.replace({ query: {} })
      })
      .catch(error => {
        // Some error occurred, you can inspect the code: error.code
        // Common errors could be invalid email and invalid or expired OTPs.
        console.error(error)
      })
  }
  return provider
}

const signInWithGoogle = async (): Promise<void> => {
  try {
    const auth = getAuth(app)
    const googleProvider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, googleProvider)
    const idToken = await result.user.getIdToken(true)
    const { payload } = decodeToken(idToken)

    const web3authProvider = await web3auth.connect({
      verifier,
      verifierId: (payload as { sub: string }).sub,
      idToken,
    })

    if (web3authProvider) {
      loggedIn.value = true
      provider.value = web3authProvider
    }
  } catch (err) {
    console.error(err)
    throw err
  }
}

const signInWithEmail = async () => {
  if (!web3auth) {
    throw new Error('web3auth not initialised yet')
  }
  window.localStorage.setItem('emailForSignIn', email.value)
  // login with firebase
  const auth = getAuth(app)
  await sendSignInLinkToEmail(auth, email.value, {
    url: document.location.origin,
    handleCodeInApp: true,
  }).catch(error => {
    console.error(error)
  })
}

web3auth.on(ADAPTER_EVENTS.CONNECTED, async _data => {
  // console.log("sfa:connected", data);
  // console.log("sfa:state", web3auth?.state);
  provider.value = web3auth.provider || undefined
  const appStore = useAppStore()
  appStore.setIsUnlocked(true)
  console.log('web3auth connected')
})

web3auth.on(ADAPTER_EVENTS.DISCONNECTED, () => {
  // console.log("sfa:disconnected");
  provider.value = web3auth.provider || undefined
  const appStore = useAppStore()
  appStore.setIsUnlocked(false)
  console.log('web3auth disconnected')
})

async function getUserName() {
  // Get the username of the current user
  return await web3auth.getUserInfo()
}

async function registerPasskey() {
  if (!pkPlugin || !web3auth) {
    throw new Error('plugin not initialized yet')
  }
  const result = shouldSupportPasskey()
  if (!result.isBrowserSupported) {
    throw new Error('Browser not supported')
  }
  const userInfo = await web3auth?.getUserInfo()
  const addresses = provider.value ? await RPC.getAccounts(provider.value) : []
  return await pkPlugin.registerPasskey({
    username:
      addresses[0] ||
      `google|${
        userInfo?.email || userInfo?.name
      } - ${new Date().toLocaleDateString('en-GB')}`,
  })
}

const signInWithPasskey = async () => {
  // Login with an existing passkey
  await pkPlugin.loginWithPasskey()
  if (web3auth.connected) {
    loggedIn.value = true
  }
}

// async function listPasskeys() {
//   // List all registered passkeys
//   const _passkeys = await pkPlugin.listAllPasskeys()
//   passkeys.value = _passkeys
//   uiConsole(_passkeys)
// }

const loggedIn = ref<boolean>(false)

const getUserInfo = async () => {
  return await web3auth.getUserInfo()
}

const logout = async () => {
  await web3auth.logout()
  provider.value = undefined
  loggedIn.value = false
}

const getAccounts = async () => {
  if (!provider.value) {
    throw new Error(PROVIDER_NOT_INITIALIZED)
  }
  return await RPC.getAccounts(provider.value)
}

const getBalance = async () => {
  if (!provider.value) {
    throw new Error(PROVIDER_NOT_INITIALIZED)
  }
  return await RPC.getBalance(provider.value)
}

const signMessage = async () => {
  if (!provider.value) {
    throw new Error(PROVIDER_NOT_INITIALIZED)
  }
  return await RPC.signMessage(provider.value)
}

const showPrivateKey = async () => {
  if (!provider.value) {
    throw new Error(PROVIDER_NOT_INITIALIZED)
  }
  return await RPC.getPrivateKey(provider.value)
}

const sendTransaction = async () => {
  if (!provider.value) {
    throw new Error(PROVIDER_NOT_INITIALIZED)
  }
  return await RPC.sendTransaction(provider.value)
}

export default function useAuth() {
  return {
    init,
    clientId,
    email,
    provider,
    loggedIn,
    signInWithGoogle,
    signInWithEmail,
    signInWithEmailLink,
    getUserName,
    registerPasskey,
    signInWithPasskey,
    getUserInfo,
    logout,
    getAccounts,
    getBalance,
    signMessage,
    showPrivateKey,
    sendTransaction,
  }
}
